import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Store, Eye, EyeOff } from "lucide-react";

export default function Register() {
  const { t, lang } = useI18n();
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    restaurant_name: "",
    email: "",
    password: "",
    password_confirm: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const mismatch = useMemo(
    () => form.password_confirm.length > 0 && form.password !== form.password_confirm,
    [form.password, form.password_confirm],
  );
  const canSubmit =
    !loading &&
    form.password.length >= 6 &&
    form.password === form.password_confirm &&
    form.full_name.trim() &&
    form.restaurant_name.trim() &&
    form.email.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (mismatch) {
      toast.error(t("passwordsMismatch"));
      return;
    }
    if (form.password !== form.password_confirm) return;
    setLoading(true);
    try {
      await register({
        full_name: form.full_name,
        restaurant_name: form.restaurant_name,
        email: form.email,
        password: form.password,
        password_confirm: form.password_confirm,
        language: lang,
      });
      toast.success(t("welcome"));
      nav("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-lg">R</div>
          <span className="font-display font-bold text-lg">RestroPilot</span>
        </div>

        <div className="rounded-2xl border-2 border-primary/20 bg-primary/5 p-4 mb-6 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
            <Store className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-semibold text-sm tracking-tight">
              {lang === "tr" ? "Restoranını RestroPilot'a taşı" : "Bring dein Restaurant zu RestroPilot"}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {lang === "tr"
                ? "Kendi tenant'ını oluştur — kimse verilerine erişemez."
                : "Erstelle deinen eigenen Tenant — niemand kann auf deine Daten zugreifen."}
            </div>
          </div>
        </div>

        <h2 className="font-display font-bold text-2xl">{t("register")}</h2>
        <p className="text-sm text-muted-foreground mt-1">
          {lang === "tr"
            ? "Aşağıdaki bilgilerle hesabını ve restoranını 30 saniyede kur."
            : "Erstelle dein Konto und Restaurant in 30 Sekunden."}
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label>{t("fullName")}</Label>
            <Input required autoComplete="name" value={form.full_name} onChange={update("full_name")} placeholder={lang === "tr" ? "Ad Soyad" : "Vor- und Nachname"} data-testid="reg-name" className="mt-1" />
          </div>
          <div>
            <Label>{t("restaurantName")}</Label>
            <Input required autoComplete="organization" value={form.restaurant_name} onChange={update("restaurant_name")} placeholder={lang === "tr" ? "Restoran adı" : "Restaurantname"} data-testid="reg-rest" className="mt-1" />
            <p className="text-[11px] text-muted-foreground mt-1">
              {lang === "tr"
                ? "Bu isim panelinin başlığı ve mutfağın adı olarak görünür."
                : "Dieser Name erscheint als Titel deines Panels."}
            </p>
          </div>
          <div>
            <Label>{t("email")}</Label>
            <Input type="email" required autoComplete="email" value={form.email} onChange={update("email")} placeholder="you@restaurant.com" data-testid="reg-email" className="mt-1" />
          </div>

          <div>
            <Label>{t("password")}</Label>
            <div className="relative mt-1">
              <Input
                type={showPwd ? "text" : "password"}
                required minLength={6} autoComplete="new-password"
                value={form.password} onChange={update("password")}
                placeholder={t("passwordMinPlaceholder")}
                data-testid="reg-password"
                className="pr-10"
              />
              <button
                type="button" onClick={() => setShowPwd(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted"
                aria-label={showPwd ? "hide" : "show"}
                data-testid="reg-password-toggle"
              >
                {showPwd ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
              </button>
            </div>
          </div>

          <div>
            <Label>{t("passwordAgain")}</Label>
            <div className="relative mt-1">
              <Input
                type={showPwd2 ? "text" : "password"}
                required minLength={6} autoComplete="new-password"
                value={form.password_confirm} onChange={update("password_confirm")}
                placeholder={t("passwordConfirmPlaceholder")}
                data-testid="reg-password-confirm"
                className={`pr-10 ${mismatch ? "border-rose-400 focus-visible:ring-rose-400" : ""}`}
                aria-invalid={mismatch}
              />
              <button
                type="button" onClick={() => setShowPwd2(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted"
                aria-label={showPwd2 ? "hide" : "show"}
                data-testid="reg-password-confirm-toggle"
              >
                {showPwd2 ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
              </button>
            </div>
            {mismatch && (
              <p className="text-xs text-rose-600 mt-1" data-testid="reg-password-mismatch">
                {t("passwordsMismatch")}
              </p>
            )}
          </div>

          <Button
            type="submit" disabled={!canSubmit}
            className="w-full h-11 active:scale-95"
            data-testid="reg-submit"
          >
            {loading ? t("loading") : t("createRestaurant")}
          </Button>
        </form>
        <div className="mt-4 text-center">
          <Link to="/login" className="text-sm text-primary hover:underline" data-testid="link-login">
            {t("goToLogin")}
          </Link>
        </div>
      </div>
    </div>
  );
}
